// eslint-disable-next-line max-lines-per-function

import { defaultAuthorizationRequest } from '../../constants/defaultAuthorizationRequest';
import { CustomDataAttrGtmClick } from '../../features/google/customDataType';
import { footerMenuUrls } from '../../features/url/footerUrl';
import { mainContentsUrls } from '../../features/url/mainContentsUrls';
import { HttpsUrl } from '../../types/HttpsUrl';
import { NurseSenkaTheme } from '../../types/NurseSenkaTheme';
import { ButtonBase } from '../Button/ButtonBase';
import { ButtonNav } from '../Button/ButtonNav';
import { ButtonText } from '../Button/ButtonText';
import {
  FiFacebookIcon,
  FiInstagramIcon,
  FiLogInIcon,
  FiTwitterIcon,
  FiUserIcon,
} from '../Icon';

import { FooterProps } from './Footer';
import styles from './Footer.module.scss';
import { PrivacyMark } from './PrivacyMark';

import type { AppEnv } from '@/packages/nurse-senka-frontend-sdk';
import type { FC } from 'react';

type InnerNavigationParam = {
  id: number;
  link: HttpsUrl;
  active?: boolean;
  text: string;
  customDataAttrGtmClick: CustomDataAttrGtmClick;
};

const createInnerNavigationParams = (
  appEnv: AppEnv,
  theme?: NurseSenkaTheme,
): InnerNavigationParam[] => {
  const urls = mainContentsUrls(appEnv);

  const activeLounge = theme?.id === 'lounge';
  const activeSquare = theme?.id === 'square';
  const activeKnowledge = theme?.id === 'knowledge';

  return [
    {
      id: 1,
      link: urls.top,
      text: 'トップ',
      customDataAttrGtmClick: 'header-inner-navi-top',
    },
    {
      id: 2,
      link: urls.knowledge,
      active: activeKnowledge,
      text: '看護知識',
      customDataAttrGtmClick: 'header-inner-navi-lounge',
    },
    {
      id: 3,
      link: urls.square,
      active: activeSquare,
      text: '看護記事',
      customDataAttrGtmClick: 'header-inner-navi-square',
    },
    {
      id: 4,
      link: urls.lounge,
      active: activeLounge,
      text: '掲示板',
      customDataAttrGtmClick: 'header-inner-navi-knowledge',
    },
  ];
};

// eslint-disable-next-line max-lines-per-function
export const FooterAbove: FC<FooterProps> = ({
  theme,
  appEnv,
  isLoggedIn,
  authorizationRequest = defaultAuthorizationRequest,
  redirectUriAfterRegistration,
}) => {
  const params = createInnerNavigationParams(appEnv, theme);
  const urls = footerMenuUrls(appEnv, authorizationRequest);

  return (
    <div className={styles['block-footer-above']}>
      <div className={styles['block-left']}>
        <ul className={styles['block-ul']}>
          {params.map((param) => (
            <li className={styles.list} key={param.id}>
              <ButtonNav
                link={param.link}
                color="white"
                active={param.active}
                theme={theme}
                customDataAttrGtmClick={param.customDataAttrGtmClick}
              >
                {param.text}
              </ButtonNav>
            </li>
          ))}
        </ul>
        <ul className={styles['block-ul']}>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.description}
              useNextLink={true}
            >
              ナース専科とは
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText color="white" link={urls.help} useNextLink={true}>
              ヘルプ
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText color="white" link={urls.contact} useNextLink={true}>
              お問い合わせ
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.termsOfService}
              useNextLink={true}
            >
              利用規約
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.operatingCompany}
              useNextLink={true}
            >
              運営会社
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.privacyPolicy}
              useNextLink={true}
            >
              個人情報について
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.advertisements}
              useNextLink={true}
            >
              広告掲載について
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.termsOfSale}
              useNextLink={true}
            >
              特定商取引法に基づく表示
            </ButtonText>
          </li>
        </ul>
        <span className={styles['list-title']}>関連サービス</span>
        <ul className={styles['block-ul']}>
          <li className={styles.list}>
            <ButtonText color="white" link={urls.njb} useNextLink={true}>
              ナース専科 転職
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.recruitNurseSenka}
              useNextLink={true}
            >
              ナース専科 就職
            </ButtonText>
          </li>
          <li className={styles.list}>
            <ButtonText
              color="white"
              link={urls.jobNurseSenka}
              useNextLink={true}
            >
              ナース専科求人ナビ
            </ButtonText>
          </li>
        </ul>
      </div>

      <div className={styles['block-right']}>
        {isLoggedIn ? (
          ''
        ) : (
          <ul className={styles['block-ul-right']}>
            <li className={styles.list}>
              <ButtonBase
                icon={FiUserIcon}
                color="primary"
                width="wide"
                link={
                  redirectUriAfterRegistration
                    ? `${urls.memberRegistration}?redirect_uri=${redirectUriAfterRegistration}`
                    : urls.memberRegistration
                }
                customDataAttrGtmClick="footer-registration-button"
              >
                会員登録
              </ButtonBase>
            </li>
            <li className={styles.list}>
              <ButtonBase
                icon={FiLogInIcon}
                color="secondary"
                width="wide"
                link={urls.login}
                customDataAttrGtmClick="footer-registration-button"
              >
                ログイン
              </ButtonBase>
            </li>
          </ul>
        )}

        <ul className={styles['block-ul-right']}>
          <li className={styles.list}>更新情報や関連情報をチェック</li>
          <li className={styles.list}>
            <ButtonBase
              icon={FiFacebookIcon}
              color="secondary"
              width="onlyIcon"
              link="https://www.facebook.com/nursesenka.info/"
              target="_blank"
              customDataAttrGtmClick="footer-facebook-button"
            />
          </li>
          <li className={styles.list}>
            <ButtonBase
              icon={FiTwitterIcon}
              color="secondary"
              width="onlyIcon"
              link="https://twitter.com/nursesenka_com"
              target="_blank"
              customDataAttrGtmClick="footer-twitter-button"
            />
          </li>
          <li className={styles.list}>
            <ButtonBase
              icon={FiInstagramIcon}
              color="secondary"
              width="onlyIcon"
              link="https://www.instagram.com/nursesenka/"
              target="_blank"
              customDataAttrGtmClick="footer-instagram-button"
            />
          </li>
        </ul>
        <ul className={styles['block-ul-right']}>
          <li className={styles.list}>
            <PrivacyMark />
          </li>
        </ul>
      </div>
    </div>
  );
};
